import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import api from '../../lib/api';
import CustomerHeader from '../../components/CustomerHeader';
import CustomerFooter from '../../components/CustomerFooter';
import ProductCard from '../../components/ProductCard';
import { LoadingBlock, EmptyState } from '../../components/States';
import { Package as PackageIcon, Search } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

export default function ProductListing() {
    const { slug } = useParams();
    const location = useLocation();
    const [params, setParams] = useSearchParams();
    const q = params.get('q') || '';
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [category, setCategory] = useState(null);
    const [sort, setSort] = useState('newest');
    const [loading, setLoading] = useState(true);
    const [priceMax, setPriceMax] = useState('all');

    useEffect(() => {
        (async () => {
            const cr = await api.get('/categories');
            setCategories(cr.data);
        })();
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const p = { limit: 200, active: true };
                let cat = null;
                if (slug) {
                    cat = categories.find(c => c.slug === slug);
                    if (cat) p.category_id = cat.id;
                }
                setCategory(cat);
                if (q) p.q = q;
                const r = await api.get('/products', { params: p });
                let list = r.data.items || [];
                if (priceMax !== 'all') {
                    const max = Number(priceMax);
                    list = list.filter(x => (x.pricing?.retail_selling_price || 0) <= max);
                }
                if (sort === 'price_asc') list = [...list].sort((a, b) => (a.pricing?.retail_selling_price || 0) - (b.pricing?.retail_selling_price || 0));
                if (sort === 'price_desc') list = [...list].sort((a, b) => (b.pricing?.retail_selling_price || 0) - (a.pricing?.retail_selling_price || 0));
                if (sort === 'name') list = [...list].sort((a, b) => a.name.localeCompare(b.name));
                setItems(list);
            } finally { setLoading(false); }
        })();
    }, [slug, q, sort, priceMax, categories]);

    const title = category ? category.name : q ? `Search: "${q}"` : 'All Products';

    return (
        <div className="bg-white min-h-screen">
            <CustomerHeader />
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="plp-title">{title}</h1>
                        <div className="text-sm text-neutral-600 mt-1">{items.length} product{items.length === 1 ? '' : 's'}</div>
                    </div>
                    <div className="flex gap-2">
                        <Select value={priceMax} onValueChange={setPriceMax}>
                            <SelectTrigger className="w-36" data-testid="plp-price-filter"><SelectValue placeholder="Price" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All prices</SelectItem>
                                <SelectItem value="250">Under ₹250</SelectItem>
                                <SelectItem value="500">Under ₹500</SelectItem>
                                <SelectItem value="1000">Under ₹1,000</SelectItem>
                                <SelectItem value="3000">Under ₹3,000</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={sort} onValueChange={setSort}>
                            <SelectTrigger className="w-40" data-testid="plp-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="newest">Newest</SelectItem>
                                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                                <SelectItem value="price_desc">Price: High to Low</SelectItem>
                                <SelectItem value="name">Name (A–Z)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                {loading ? <LoadingBlock /> : items.length === 0 ? (
                    <EmptyState icon={PackageIcon} title="No products found" description="Try adjusting filters or search terms." />
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="plp-grid">
                        {items.map(p => <ProductCard key={p.id} product={p} />)}
                    </div>
                )}
            </div>
            <CustomerFooter />
        </div>
    );
}
